// =============================================================================
// MiniCalendar - Calendario dropdown riutilizzabile
// =============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDateLocale } from '../../hooks/useDateLocale';
import { cn } from '../../lib/utils';

interface MiniCalendarProps {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  onClose: () => void;
  /** Allineamento dropdown: left (default) o right */
  align?: 'left' | 'right';
}

export function MiniCalendar({ selectedDate, onSelect, onClose, align = 'left' }: MiniCalendarProps) {
  const [viewMonth, setViewMonth] = useState(startOfMonth(selectedDate));
  const dateLocale = useDateLocale();
  const calRef = useRef<HTMLDivElement>(null);

  // Chiudi con click fuori
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (calRef.current && !calRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Chiudi con Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSelect = useCallback((day: Date) => {
    onSelect(day);
    onClose();
  }, [onSelect, onClose]);

  // Genera griglia giorni
  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const weeks: Date[][] = [];
  let day = gridStart;
  while (day <= gridEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  // Header nomi giorni
  const dayNames = Array.from({ length: 7 }, (_, i) =>
    format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i), 'EEEEE', { locale: dateLocale })
  );

  return (
    <div
      ref={calRef}
      className={cn(
        'absolute top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-3 w-[280px] select-none',
        align === 'right' ? 'right-0' : 'left-0',
      )}
    >
      {/* Header mese */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setViewMonth(subMonths(viewMonth, 1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>
        <span className="text-sm font-semibold text-gray-900 capitalize">
          {format(viewMonth, 'MMMM yyyy', { locale: dateLocale })}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth(addMonths(viewMonth, 1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Griglia */}
      <div className="grid grid-cols-7 gap-0.5">
        {/* Nomi giorni */}
        {dayNames.map((name, i) => (
          <div key={i} className="h-8 flex items-center justify-center text-[11px] font-medium text-gray-400 uppercase">
            {name}
          </div>
        ))}

        {/* Giorni */}
        {weeks.flatMap((week) =>
          week.map((d) => {
            const inMonth = isSameMonth(d, viewMonth);
            const selected = isSameDay(d, selectedDate);
            const today_ = isToday(d);

            return (
              <button
                type="button"
                key={d.toISOString()}
                onClick={() => handleSelect(d)}
                className={cn(
                  'h-8 w-8 mx-auto rounded-lg text-sm transition-all duration-100',
                  'hover:bg-emerald-50 hover:text-emerald-700',
                  !inMonth && 'text-gray-300',
                  inMonth && !selected && 'text-gray-700',
                  today_ && !selected && 'font-bold text-emerald-600 ring-1 ring-emerald-200',
                  selected && 'bg-emerald-500 text-white font-semibold shadow-sm hover:bg-emerald-600 hover:text-white',
                )}
              >
                {format(d, 'd')}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
